import {
  useState,
  useEffect,
  useCallback,
  type JSX,
} from "react"
import { useAuth } from "@/hooks/useAuth"
import { FilePlusCorner, Plus, Upload, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useFileUpload } from "@/hooks/use-file-upload.ts"
import SingleSelectCombobox, { type ComboboxOption } from "@/components/shared/SingleSelectCombobox"
import { addReportToSurvey, addSurvey, addSurveyType, getSurveysList, getSurveyTypes, NewSurveyRequest, SurveyType } from "@/services/surveysListService.ts"
import { getCountryToIso3Map } from "@/services/mapAdminInfoService.ts"
import { addFilesToSource, addReportToBiblio, NewReportRequest } from "@/services/biblioService.ts"
import { useNavigate } from "react-router-dom"

const ADD_NEW_SURVEY_TYPE_VALUE = "__add_new_survey_type__"



export default function NewSurveyForm(): JSX.Element {
  const { getAccessToken, user } = useAuth()
  const navigate = useNavigate()

  /** Whether the form is in edit mode (has an existing survey). */

  const [
    { files, isDragging },
    {
      openFileDialog,
      removeFile,
      getInputProps,
      clearFiles,
      handleDragEnter,
      handleDragLeave,
      handleDragOver,
      handleDrop,
    },
  ] = useFileUpload({
    accept: "*",
    multiple: false,
  })

  // Country-to-ISO3 map loaded from the MAP data API
  const [countryToIso3, setCountryToIso3] = useState<Map<string, string>>(new Map())
  const [countriesLoading, setCountriesLoading] = useState<boolean>(true)
  const [countriesError, setCountriesError] = useState<string | null>(null)

  // Form state — required fields
  const [countryName, setCountryName] = useState<string>("")
  const [surveyType, setSurveyType] = useState<string>("")
  const [year, setYear] = useState<string>("")
  // Targeted population scope. Required; defaults to "National".
  const [populationRepresentativeness, setPopulationRepresentativeness] =
    useState<"National" | "Subnational" | "School">("National")

  const [addingNewType, setAddingNewType] = useState<boolean>(false)
  const [newTypeCode, setNewTypeCode] = useState<string>("")
  const [newTypeMeaning, setNewTypeMeaning] = useState<string>("")
  const [surveyTypeOptions, setSurveyTypeOptions] = useState<SurveyType[]>([])
  const [surveyTypesError, setSurveyTypesError] = useState<string | null>(null)

  // Form state — optional fields
  const [biblioIdsRaw, setBiblioIdsRaw] = useState<string>("")
  const [dhsSurveyId, setDhsSurveyId] = useState<string>("")
  const [reportBiblioId, setReportBiblioId] = useState<string>("")
  const [reportBiblioFile, setReportBiblioFile] = useState<string>("")
  const [surveyStatus, setSurveyStatus] = useState<string>("")

  // Distinct survey status values that already exist in the catalogue, mirroring
  // the data-driven Survey Status filter on the catalogue page.
  const [surveyStatusOptions, setSurveyStatusOptions] = useState<string[]>([])
  const [surveyStatusError, setSurveyStatusError] = useState<string | null>(null)

  // Biblio source mode: null (not selected), "new", or "existing"
  const [biblioMode, setBiblioMode] = useState<"new" | "existing" | null>(null)

  // Form state — new biblio source fields
  const [reportTitle, setReportTitle] = useState<string>("")
  const [publicationYear, setPublicationYear] = useState<string>("")
  const [authors, setAuthors] = useState<string>("")
  const [institutions, setInstitutions] = useState<string>("")
  const [city, setCity] = useState<string>("")
  const [reportNumber, setReportNumber] = useState<string>("")
  const [pages, setPages] = useState<string>("")

  // Submission state
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null)

  // Fetch country-to-ISO3 map on mount
  useEffect(() => {
    async function loadCountries(): Promise<void> {
      try {
        const map = await getCountryToIso3Map()
        setCountryToIso3(map)
      } catch (err) {
        setCountriesError(err instanceof Error ? err.message : "Failed to load countries")
      } finally {
        setCountriesLoading(false)
      }
    }
    loadCountries()
  }, [])

  useEffect(() => {
    async function loadSurveyTypes(): Promise<void> {
      try {
        const token = await getAccessToken()
        const types: SurveyType[] = await getSurveyTypes(token)
        // Sort alphabetically by surveyType code
        const sorted = [...types].sort((a, b) => a.surveyType.localeCompare(b.surveyType))
        setSurveyTypeOptions(sorted)
      } catch (err) {
        setSurveyTypesError(err instanceof Error ? err.message : "Failed to load survey types")
      }
    }
    loadSurveyTypes()
  }, [getAccessToken])

  // Fetch the existing surveys once on mount and derive the distinct survey
  // status values, mirroring how the catalogue's Survey Status filter is built.
  useEffect(() => {
    async function loadSurveyStatuses(): Promise<void> {
      try {
        const token = await getAccessToken()
        const surveys = await getSurveysList(token)
        const distinctStatuses: string[] = [...new Set(surveys.map(survey => survey.surveyStatus))]
          .filter((status): status is string => typeof status === "string" && status.trim().length > 0)
          .sort()
        setSurveyStatusOptions(distinctStatuses)
      } catch (err) {
        setSurveyStatusError(err instanceof Error ? err.message : "Failed to load survey statuses")
      }
    }
    loadSurveyStatuses()
  }, [getAccessToken])

  /**
   * Reset all form fields to their initial empty state.
   */
  const resetForm = useCallback((): void => {
    setCountryName("")
    setSurveyType("")
    setAddingNewType(false)
    setNewTypeCode("")
    setNewTypeMeaning("")
    setYear("")
    setPopulationRepresentativeness("National")
    setBiblioIdsRaw("")
    setDhsSurveyId("")
    setReportBiblioId("")
    setReportBiblioFile("")
    setSurveyStatus("")
    setBiblioMode(null)
    setReportTitle("")
    setPublicationYear("")
    setAuthors("")
    setInstitutions("")
    setCity("")
    setReportNumber("")
    setPages("")
    setSubmitError(null)
  }, [])

  /**
   * Handle form submission. Validates required fields, generates the
   * mapSurveyId, and calls the addSurvey service. Then, depending on the
   * biblio mode:
   * - "new": creates a biblio source via addReportToBiblio, then links it
   *   to the survey via addReportToSurvey.
   * - "existing": if a file was uploaded, calls addFilesToSource to attach
   *   it, then links the source to the survey via addReportToSurvey.
   */
  const handleSubmit = useCallback(async (): Promise<void> => {
    setSubmitError(null)
    setSubmitSuccess(null)

    // Validate required fields
    if (!countryName) {
      setSubmitError("Country is required.")
      return
    }

    let effectiveSurveyType: string
    if (addingNewType) {
      const trimmedCode: string = newTypeCode.trim()
      const trimmedMeaning: string = newTypeMeaning.trim()
      if (!trimmedCode) {
        setSubmitError("New survey type code is required.")
        return
      }
      if (!trimmedMeaning) {
        setSubmitError("A meaning is required when adding a new survey type.")
        return
      }
      effectiveSurveyType = trimmedCode
    } else {
      if (!surveyType) {
        setSubmitError("Survey type is required.")
        return
      }
      effectiveSurveyType = surveyType
    }

    if (!year) {
      setSubmitError("Year is required.")
      return
    }

    const yearNum = parseInt(year, 10)
    if (isNaN(yearNum)) {
      setSubmitError("Year must be a valid number.")
      return
    }

    // Look up ISO3 for the selected country
    const iso3 = countryToIso3.get(countryName)
    if (!iso3) {
      setSubmitError(`Could not find ISO3 code for country: ${countryName}`)
      return
    }

    // Validate biblio mode-specific fields
    if (biblioMode === "new") {
      if (!reportTitle.trim()) {
        setSubmitError("Title is required when creating a new source.")
        return
      }
      if (files.length === 0) {
        setSubmitError("A report file is required when creating a new source.")
        return
      }
    }
    if (biblioMode === "existing") {
      if (!reportBiblioId.trim()) {
        setSubmitError("Source ID is required when adding an existing source.")
        return
      }
    }

    // Generate mapSurveyId: iso3 + year + surveyType
    const mapSurveyId = `${iso3}${yearNum}${effectiveSurveyType}`

    // Parse biblioIds from comma-separated string
    const biblioIds: string[] = biblioIdsRaw
      .split(",")
      .map(id => id.trim())
      .filter(id => id.length > 0)

    const newSurveyRequest: NewSurveyRequest = {
      mapSurveyId,
      countryName,
      countryIso3: iso3,
      year: yearNum,
      surveyType: effectiveSurveyType,
      populationRepresentativeness,
      createdBy: user?.email ?? "unknown",
      biblioIds,
      dhsSurveyId,
      reportBiblioId: biblioMode === "existing" ? reportBiblioId : undefined,
      reportBiblioFile,
      surveyStatus,
    }

    // NOTE: When `addingNewType` is true, `newTypeMeaning` holds the
    // human-readable meaning for the new survey type. The new type is
    // persisted to the malaria-survey-types table via addSurveyType below.

    setSubmitting(true)
    try {
      const token = await getAccessToken()

      // Step 0: If adding a new survey type, persist it to the table
      if (addingNewType) {
        const updatedTypes: SurveyType[] = await addSurveyType(token, {
          surveyType: effectiveSurveyType,
          description: newTypeMeaning.trim() || undefined,
        })
        // Refresh the local survey type options with the updated list
        const sorted = [...updatedTypes].sort((a, b) => a.surveyType.localeCompare(b.surveyType))
        setSurveyTypeOptions(sorted)
      }

      // Step 1: Create the survey
      const createdMapSurveyId: string = await addSurvey(token, newSurveyRequest)

      // Step 2: Handle biblio source linking
      if (biblioMode === "new" && createdMapSurveyId !== undefined) {
        // Parse authors from semicolon-separated string
        const authorsList: string[] = authors
          .split(";")
          .map(author => author.trim())
          .filter(author => author.length > 0)

        // Parse institutions from comma-separated string
        const institutionsList: string[] = institutions
          .split(",")
          .map(institution => institution.trim())
          .filter(institution => institution.length > 0)

        const newReportRequest: NewReportRequest = {
          title: reportTitle.trim(),
          files,
          mapSurveyId,
          surveyType: effectiveSurveyType,
          publicationYear: publicationYear.trim(),
          authors: authorsList,
          institutions: institutionsList,
          reportNumber: reportNumber.trim(),
          pages: pages.trim(),
          city: city.trim(),
          createdBy: user?.email ?? "unknown",
        }

        // Create the biblio source and upload file to Google Drive
        const reportResponse = await addReportToBiblio(token, newReportRequest)

        // Link the report to the survey
        const sourceFileId: string | undefined = reportResponse.driveFileId
          ? `https://drive.google.com/file/d/${reportResponse.driveFileId}/view`
          : undefined

        await addReportToSurvey(token, {
          sourceId: String(reportResponse.sourceId),
          sourceFileId,
          mapSurveyId,
          surveyType: effectiveSurveyType,
        })
      } else if (biblioMode === "existing" && createdMapSurveyId !== undefined) {
        let sourceFileId: string | undefined

        // If a file was uploaded, add it to the existing source
        if (files.length > 0) {
          const addFilesResponse = await addFilesToSource(token, reportBiblioId.trim(), files)

          const driveFileId: string | undefined = addFilesResponse.driveFileIds?.[0]

          sourceFileId = driveFileId
            ? `https://drive.google.com/file/d/${driveFileId}/view`
            : undefined
        }

        // Link the existing source to the survey
        await addReportToSurvey(token, {
          sourceId: reportBiblioId.trim(),
          sourceFileId,
          mapSurveyId,
          surveyType: effectiveSurveyType,
        })
      }
      resetForm()
      navigate("/survey-catalogue")
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to create survey")
    } finally {
      setSubmitting(false)
    }
  }, [
    countryName,
    surveyType,
    addingNewType,
    newTypeCode,
    newTypeMeaning,
    year,
    populationRepresentativeness,
    biblioIdsRaw,
    dhsSurveyId,
    reportBiblioId,
    reportBiblioFile,
    surveyStatus,
    biblioMode,
    reportTitle,
    files,
    publicationYear,
    authors,
    institutions,
    city,
    reportNumber,
    pages,
    countryToIso3,
    user,
    getAccessToken,
    resetForm,
    navigate,
  ])

  // Sorted list of country names from the map
  const sortedCountries: string[] = [...countryToIso3.keys()].sort()

  // Country options for the combobox: label and value are the country name.
  const countryOptions: ComboboxOption[] = sortedCountries.map(name => ({
    value: name,
    label: name,
  }))

  return (
    <>
    <title>New Survey - MAP Apps Platform</title>
    <div className="mx-auto w-full max-w-2xl p-6">
      <Card>
        <CardHeader>
          <CardTitle><h1>Create a new Survey</h1></CardTitle>
          <CardDescription>
            Add a new survey to the catalogue. Required fields are marked with *.
          </CardDescription>
        </CardHeader>

        <CardContent className="grid gap-4">
          {/* Country Name — required */}
          <div className="grid gap-1.5">
            <label htmlFor="country-select" className="text-sm font-medium">
              Country *
            </label>
            {countriesLoading ? (
              <p className="text-sm text-muted-foreground">Loading countries…</p>
            ) : countriesError ? (
              <p className="text-sm text-destructive">{countriesError}</p>
            ) : (
              <SingleSelectCombobox
                id="country-select"
                options={countryOptions}
                value={countryName}
                onChange={setCountryName}
                placeholder="Select a country"
                emptyMessage="No countries found."
              />
            )}
          </div>

          {/* Survey Type — required */}
          <div className="grid gap-1.5">
            <label htmlFor="survey-type" className="text-sm font-medium">
              Survey Type *
            </label>
            <Select
              value={addingNewType ? ADD_NEW_SURVEY_TYPE_VALUE : surveyType}
              onValueChange={value => {
                if (value === ADD_NEW_SURVEY_TYPE_VALUE) {
                  setAddingNewType(true)
                  setSurveyType("")
                } else {
                  setAddingNewType(false)
                  setSurveyType(value)
                  setNewTypeCode("")
                  setNewTypeMeaning("")
                }
              }}
            >
              <SelectTrigger id="survey-type" className="w-full">
                <SelectValue placeholder="Select a survey type" />
              </SelectTrigger>
              <SelectContent>
                {surveyTypeOptions.map(typeObj => (
                  <SelectItem key={typeObj.surveyType} value={typeObj.surveyType}>
                    {typeObj.description
                      ? `${typeObj.surveyType} (${typeObj.description})`
                      : typeObj.surveyType}
                  </SelectItem>
                ))}
                <SelectItem value={ADD_NEW_SURVEY_TYPE_VALUE}>+ Add a new survey type…</SelectItem>
              </SelectContent>
            </Select>
            {surveyTypesError && <p className="text-xs text-destructive">{surveyTypesError}</p>}

            {/* New survey type — code and mandatory meaning */}
            {addingNewType && (
              <div className="grid gap-3 mt-1 rounded-md border border-border/60 p-3">
                <div className="grid gap-1.5">
                  <label htmlFor="new-survey-type-code" className="text-sm font-medium">
                    New type code *
                  </label>
                  <Input
                    id="new-survey-type-code"
                    placeholder="e.g. AIS"
                    value={newTypeCode}
                    onChange={e => setNewTypeCode(e.target.value)}
                  />
                </div>
                <div className="grid gap-1.5">
                  <label htmlFor="new-survey-type-meaning" className="text-sm font-medium">
                    Meaning *
                  </label>
                  <Input
                    id="new-survey-type-meaning"
                    placeholder="e.g. AIDS Indicator Survey"
                    value={newTypeMeaning}
                    onChange={e => setNewTypeMeaning(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    The full meaning of the acronym. Required for new types.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Year — required */}
          <div className="grid gap-1.5">
            <label htmlFor="survey-year" className="text-sm font-medium">
              Year *
            </label>
            <Input
              id="survey-year"
              type="number"
              placeholder="e.g. 2023"
              value={year}
              onChange={e => setYear(e.target.value)}
            />
          </div>

          {/* Targeted Population — required, defaults to National */}
          <div className="grid gap-1.5">
            <label htmlFor="population-representativeness" className="text-sm font-medium">
              Targeted Population *
            </label>
            <Select
              value={populationRepresentativeness}
              onValueChange={value =>
                setPopulationRepresentativeness(value as "National" | "Subnational" | "School")
              }
            >
              <SelectTrigger id="population-representativeness" className="w-full">
                <SelectValue placeholder="Select a targeted population" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="National">National</SelectItem>
                <SelectItem value="Subnational">Subnational</SelectItem>
                <SelectItem value="School">School</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Generated ID preview */}
          {countryName && (addingNewType ? newTypeCode.trim() : surveyType) && year && (
            <div className="rounded-md bg-muted px-3 py-2 text-sm">
              <span className="text-muted-foreground">Generated ID: </span>
              <span className="font-mono font-medium">
                {countryToIso3.get(countryName) || "???"}
                {year}
                {addingNewType ? newTypeCode.trim() : surveyType}
              </span>
            </div>
          )}

          {/* Survey Status — optional */}
          <div className="grid gap-1.5">
            <label htmlFor="survey-status" className="text-sm font-medium">
              Survey Status
            </label>
            <Select value={surveyStatus} onValueChange={setSurveyStatus}>
              <SelectTrigger id="survey-status" className="w-full">
                <SelectValue placeholder="Select a survey status" />
              </SelectTrigger>
              <SelectContent>
                {surveyStatusOptions.map(status => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {surveyStatusError && (
              <p className="text-xs text-destructive">{surveyStatusError}</p>
            )}
          </div>

          {/* Biblio Source section */}

          <div className="pt-2">
            <p className="text-sm font-medium text-muted-foreground mb-3">
              To upload a report file, create new report biblio source to link to survey (or link
              existing source)
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={biblioMode === "new" ? "default" : "outline"}
                size="sm"
                onClick={() => setBiblioMode("new")}
              >
                <Plus className="size-4 mr-0.5" />
                Create New Source
              </Button>
              <Button
                type="button"
                variant={biblioMode === "existing" ? "default" : "outline"}
                size="sm"
                onClick={() => setBiblioMode("existing")}
              >
                <FilePlusCorner className="size-4 mr-0.5" />
                Add Existing Source
              </Button>
            </div>

            {/* Existing source — enter sourceId */}
            {biblioMode === "existing" && (
              <div className="grid gap-4 mt-3">
                <div className="grid gap-1.5">
                  <label htmlFor="existing-source-id" className="text-sm font-medium">
                    Source ID *
                  </label>
                  <Input
                    id="existing-source-id"
                    placeholder="e.g. 1001461050"
                    value={reportBiblioId}
                    onChange={e => setReportBiblioId(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter the numeric biblio source ID to link to this survey.
                  </p>
                </div>

                {/* Report File — optional */}
                <div className="grid gap-1.5">
                  <label htmlFor="existing-source-file-upload" className="text-sm font-medium">
                    Report File
                  </label>
                  <div className="space-y-2">
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={openFileDialog}
                      onKeyDown={e => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault()
                          openFileDialog()
                        }
                      }}
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      data-dragging={isDragging || undefined}
                      className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-input p-6 text-center cursor-pointer transition-colors hover:bg-accent/50 data-[dragging=true]:border-primary data-[dragging=true]:bg-accent"
                    >
                      <Upload className="size-5 text-muted-foreground" />
                      <p className="text-sm">
                        <span className="font-medium">Choose a file</span> or drag and drop here
                      </p>
                    </div>
                    <input {...getInputProps()} className="sr-only" />
                    {files.length > 0 && (
                      <ul className="space-y-1">
                        {files.map(file => (
                          <li key={file.id} className="flex items-center gap-2 text-sm">
                            <span className="truncate">{file.file.name}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="size-5 p-0"
                              onClick={() => removeFile(file.id)}
                              aria-label={`Remove ${file.file.name}`}
                            >
                              <X className="size-3" />
                            </Button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Optionally upload a file to attach to the existing source.
                  </p>
                </div>
              </div>
            )}

            {/* New source — title, file, and optional metadata */}
            {biblioMode === "new" && (
              <div className="grid gap-4 mt-3">
                {/* Title — required */}
                <div className="grid gap-1.5">
                  <label htmlFor="new-source-title" className="text-sm font-medium">
                    Title *
                  </label>
                  <Input
                    id="new-source-title"
                    placeholder="e.g. National Malaria Survey Report 2023"
                    value={reportTitle}
                    onChange={e => setReportTitle(e.target.value)}
                  />
                </div>

                {/* File Upload — required */}
                <div className="grid gap-1.5">
                  <label htmlFor="new-source-file-upload" className="text-sm font-medium">
                    Report File *
                  </label>
                  <div className="space-y-2">
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={openFileDialog}
                      onKeyDown={e => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault()
                          openFileDialog()
                        }
                      }}
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      data-dragging={isDragging || undefined}
                      className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-input p-6 text-center cursor-pointer transition-colors hover:bg-accent/50 data-[dragging=true]:border-primary data-[dragging=true]:bg-accent"
                    >
                      <Upload className="size-5 text-muted-foreground" />
                      <p className="text-sm">
                        <span className="font-medium">Choose a file</span> or drag and drop here
                      </p>
                    </div>
                    <input {...getInputProps()} className="sr-only" />
                    {files.length > 0 && (
                      <ul className="space-y-1">
                        {files.map(file => (
                          <li key={file.id} className="flex items-center gap-2 text-sm">
                            <span className="truncate">{file.file.name}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="size-5 p-0"
                              onClick={() => removeFile(file.id)}
                              aria-label={`Remove ${file.file.name}`}
                            >
                              <X className="size-3" />
                            </Button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                {/* Publication Year */}
                <div className="grid gap-1.5">
                  <label htmlFor="new-source-publication-year" className="text-sm font-medium">
                    Publication Year
                  </label>
                  <Input
                    id="new-source-publication-year"
                    type="number"
                    placeholder="e.g. 2023"
                    value={publicationYear}
                    onChange={e => setPublicationYear(e.target.value)}
                  />
                </div>

                {/* Authors */}
                <div className="grid gap-1.5">
                  <label htmlFor="new-source-authors" className="text-sm font-medium">
                    Authors
                  </label>
                  <Input
                    id="new-source-authors"
                    placeholder="e.g. Smith, John; Doe, Jane"
                    value={authors}
                    onChange={e => setAuthors(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter authors in "Last, First" format, separated by semicolons.
                  </p>
                </div>

                {/* Institutions */}
                <div className="grid gap-1.5">
                  <label htmlFor="new-source-institutions" className="text-sm font-medium">
                    Institutions
                  </label>
                  <Input
                    id="new-source-institutions"
                    placeholder="Comma-separated, e.g. WHO, Ministry of Health"
                    value={institutions}
                    onChange={e => setInstitutions(e.target.value)}
                  />
                </div>

                {/* City */}
                <div className="grid gap-1.5">
                  <label htmlFor="new-source-city" className="text-sm font-medium">
                    City
                  </label>
                  <Input
                    id="new-source-city"
                    placeholder="e.g. Geneva"
                    value={city}
                    onChange={e => setCity(e.target.value)}
                  />
                </div>

                {/* Report Number */}
                <div className="grid gap-1.5">
                  <label htmlFor="new-source-report-number" className="text-sm font-medium">
                    Report Number
                  </label>
                  <Input
                    id="new-source-report-number"
                    placeholder="e.g. WHO/HTM/GMP/2023.01"
                    value={reportNumber}
                    onChange={e => setReportNumber(e.target.value)}
                  />
                </div>

                {/* Pages */}
                <div className="grid gap-1.5">
                  <label htmlFor="new-source-pages" className="text-sm font-medium">
                    Pages
                  </label>
                  <Input
                    id="new-source-pages"
                    placeholder="e.g. 1-120"
                    value={pages}
                    onChange={e => setPages(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Submit error / success */}
          {submitError && (
            <p className="text-sm text-destructive" role="alert">
              {submitError}
            </p>
          )}
          {submitSuccess && (
            <p className="text-sm text-green-600" role="status">
              {submitSuccess}
            </p>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                resetForm()
                clearFiles()
                navigate("/survey-catalogue")
              }}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? "Creating…" : "Create Survey"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
    </>
  )
}